import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

export default function EmailFinder({ api }) {
  const [searchResults, setSearchResults] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('domain'); // domain, single, bulk, verify
  
  // Domain search state
  const [domainSearch, setDomainSearch] = useState({
    domain: '',
    results: null,
    loading: false,
    allEmails: [], // Store all emails found across searches
    pagination: {
      limit: 20,
      offset: 0,
      has_more: false,
      total_available: 0,
      excluded_count: 0
    }
  });

  // Single search state
  const [singleSearch, setSingleSearch] = useState({
    first_name: '',
    last_name: '',
    company: '',
    domain: ''
  });

  // Bulk search state
  const [bulkList, setBulkList] = useState('');
  const [bulkResults, setBulkResults] = useState([]);

  // Verification state
  const [emailsToVerify, setEmailsToVerify] = useState('');
  const [verificationResults, setVerificationResults] = useState([]);

  // Convert to CRM states
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertType, setConvertType] = useState('lead'); // lead, deal
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [convertLoading, setConvertLoading] = useState(false);
  const [funnels, setFunnels] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState('');

  const [stats, setStats] = useState({
    total_searches: 0,
    found_emails: 0,
    verified_emails: 0,
    confidence_rate: 0
  });

  useEffect(() => {
    fetchStats();
    fetchSearchHistory();
    fetchFunnels();
    fetchPipelines();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/prospecting/email-finder/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSearchHistory = async () => {
    try {
      const response = await api.get('/prospecting/email-finder/history');
      setSearchHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchFunnels = async () => {
    try {
      const response = await api.get('/funnels');
      setFunnels(response.data || []);
    } catch (error) {
      console.error('Error fetching funnels:', error);
    }
  };

  const fetchPipelines = async () => {
    try {
      const response = await api.get('/pipelines');
      setPipelines(response.data || []);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    }
  };

  // Domain search function
  const handleDomainSearch = async (isLoadMore = false) => {
    if (!domainSearch.domain.trim()) {
      toast.error('Por favor, insira um domínio válido');
      return;
    }

    setDomainSearch(prev => ({ ...prev, loading: true }));

    try {
      // If it's a new search, reset offset and emails
      const offset = isLoadMore ? domainSearch.pagination.offset + domainSearch.pagination.limit : 0;
      const excludedEmails = isLoadMore ? domainSearch.allEmails.map(e => e.email) : [];
      
      console.log('🔍 Buscando emails para domínio:', domainSearch.domain, `(offset: ${offset}, excluded: ${excludedEmails.length})`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/domain-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain: domainSearch.domain,
          limit: 20,
          offset: offset,
          excluded_emails: excludedEmails
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('📧 Resultado da busca:', result);
        
        if (result.success) {
          setDomainSearch(prev => {
            const newAllEmails = isLoadMore 
              ? [...prev.allEmails, ...result.emails]
              : result.emails;
              
            return {
              ...prev, 
              results: {
                ...result,
                emails: newAllEmails,
                total_emails: newAllEmails.length
              },
              allEmails: newAllEmails,
              pagination: result.pagination,
              loading: false 
            };
          });
          
          const message = isLoadMore 
            ? `${result.total_emails} novos emails encontrados!` 
            : `${result.total_emails} emails encontrados para ${result.domain} (limite: 20 por busca)`;
          
          toast.success(message);
          
          // Add to search history only for new searches
          if (!isLoadMore) {
            const historyItem = {
              id: Date.now(),
              type: 'domain',
              domain: result.domain,
              results: result.total_emails,
              timestamp: new Date().toISOString()
            };
            setSearchHistory(prev => [historyItem, ...prev.slice(0, 9)]);
          }
          
        } else {
          toast.error(result.error || 'Erro na busca por domínio');
        }
      } else {
        throw new Error(`Erro ${response.status}`);
      }

    } catch (error) {
      console.error('❌ Erro na busca por domínio:', error);
      toast.error('Erro ao buscar emails do domínio');
    } finally {
      setDomainSearch(prev => ({ ...prev, loading: false }));
    }
  };

  // Single email search function
  const handleSingleSearch = async () => {
    if (!singleSearch.first_name || !singleSearch.company) {
      toast.error('Nome e empresa são obrigatórios');
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 Single search data being sent:', singleSearch);
      
      // Always use the working prospecting endpoint
      const response = await api.post('/prospecting/email-finder/search', singleSearch);
      const results = response.data.results || [];
      
      setSearchResults(results);
      
      if (results && results.length > 0) {
        // Check if we have real data or mock data
        const hasRealData = results.some(r => r.source === 'hunter.io');
        const hasMockData = results.some(r => r.source === 'mock_data');
        
        if (hasRealData) {
          toast.success(`🎯 ${results.length} email(s) real(is) encontrado(s) via Hunter.io!`);
        } else if (hasMockData && singleSearch.domain) {
          toast.warning(`⚠️ ${results.length} email(s) simulado(s) - Hunter.io pode não ter dados para este domínio`);
        } else if (hasMockData && !singleSearch.domain) {
          toast.warning(`⚠️ ${results.length} email(s) simulado(s) - adicione domínio para busca real via Hunter.io`);
        } else {
          toast.success(`🎯 ${results.length} email(s) encontrado(s)!`);
        }
      } else {
        toast.warning('Nenhum email encontrado com os critérios informados');
      }
      
      // Add to search history
      const historyItem = {
        id: Date.now(),
        type: 'single',
        query: `${singleSearch.first_name} ${singleSearch.last_name} - ${singleSearch.company}`,
        results: results.length,
        timestamp: new Date().toISOString()
      };
      setSearchHistory(prev => [historyItem, ...prev.slice(0, 9)]);
      
      fetchStats();
      fetchSearchHistory();
    } catch (error) {
      toast.error('Erro ao buscar emails');
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSearch = async () => {
    if (!bulkList.trim()) {
      toast.error('Adicione ao menos um nome e empresa para busca');
      return;
    }

    setBulkLoading(true);
    try {
      // Parse bulk list (expect format: "Nome Sobrenome, Empresa")
      const entries = bulkList.split('\n').filter(line => line.trim()).map(line => {
        const parts = line.split(',');
        if (parts.length >= 2) {
          const fullName = parts[0].trim();
          const nameParts = fullName.split(' ');
          return {
            first_name: nameParts[0],
            last_name: nameParts.slice(1).join(' '),
            company: parts[1].trim(),
            domain: parts[2]?.trim() || ''
          };
        }
        return null;
      }).filter(entry => entry !== null);

      const response = await api.post('/prospecting/email-finder/bulk-search', { entries });
      setBulkResults(response.data.results || []);
      
      const foundCount = response.data.results?.filter(r => r.emails && r.emails.length > 0).length || 0;
      toast.success(`🎯 Processados ${entries.length} registros, ${foundCount} emails encontrados!`);
      
      fetchStats();
    } catch (error) {
      toast.error('Erro ao processar busca em lote');
      console.error('Bulk search error:', error);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleEmailVerification = async () => {
    if (!emailsToVerify.trim()) {
      toast.error('Adicione emails para verificação');
      return;
    }

    setLoading(true);
    try {
      const emails = emailsToVerify.split('\n').filter(email => email.trim());
      console.log('🔍 Emails para verificação:', emails);
      
      // Use the working prospecting endpoint for verification
      const response = await api.post('/prospecting/email-finder/verify', { emails });
      const results = response.data.results || [];
      
      setVerificationResults(results);
      
      const validCount = results.filter(r => r.status === 'valid' || r.status === 'risky').length;
      const mockCount = results.filter(r => r.source === 'mock_data').length;
      
      if (mockCount === results.length && mockCount > 0) {
        toast.warning(`⚠️ Verificados ${emails.length} emails (verificação básica - Hunter.io pode não ter dados)`);
      } else if (validCount > 0) {
        toast.success(`✅ Verificados ${emails.length} emails, ${validCount} válidos/arriscados!`);
      } else {
        toast.warning(`⚠️ Verificados ${emails.length} emails, nenhum válido encontrado`);
      }
      
      fetchStats();
    } catch (error) {
      toast.error('Erro ao verificar emails');
      console.error('Verification error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportResults = (results, filename) => {
    const csv = results.map(result => {
      if (result.emails) {
        return result.emails.map(email => 
          `${result.first_name || ''},${result.last_name || ''},${result.company || ''},${email.email},${email.confidence || 0}`
        ).join('\n');
      }
      return `${result.email || ''},${result.status || ''},${result.confidence || 0}`;
    }).join('\n');

    const blob = new Blob([`Nome,Sobrenome,Empresa,Email,Confiança\n${csv}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Arquivo exportado com sucesso! 📊');
  };

  const handleConvertToLead = async (emails) => {
    try {
      setConvertLoading(true);
      
      // Validate required data
      if (!selectedFunnel) {
        toast.error('Selecione um funil para os leads');
        return;
      }
      
      if (!emails || emails.length === 0) {
        toast.error('Nenhum email selecionado para conversão');
        return;
      }
      
      const convertPromises = emails.map(async (emailData) => {
        const leadData = {
          name: `${emailData.first_name || ''} ${emailData.last_name || ''}`.trim() || emailData.name || emailData.email.split('@')[0],
          email: emailData.email,
          company: emailData.company || emailData.organization || '',
          source: 'email_finder',
          funnel_id: selectedFunnel,
          notes: `Convertido do Email Finder. Confiança: ${emailData.confidence || 0}%. Fonte: ${emailData.source || 'domain_search'}`
        };
        
        // Add position if available
        if (emailData.position) {
          leadData.position = emailData.position;
        }
        
        console.log('📧 Converting email to lead:', leadData);
        console.log('🔗 API object:', api);
        console.log('🌍 Backend URL:', process.env.REACT_APP_BACKEND_URL);
        
        return api.post('/api/leads', leadData);
      });

      await Promise.all(convertPromises);
      toast.success(`🎯 ${emails.length} email(s) convertido(s) para leads com sucesso!`);
      setShowConvertModal(false);
      setSelectedEmails([]);
      
    } catch (error) {
      console.error('❌ Error converting to leads:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast.error(`Erro ao converter emails para leads: ${errorMessage}`);
    } finally {
      setConvertLoading(false);
    }
  };

  const handleConvertToDeal = async (emails) => {
    try {
      setConvertLoading(true);
      
      // Validate required data
      if (!selectedPipeline) {
        toast.error('Selecione um pipeline para os negócios');
        return;
      }
      
      if (!emails || emails.length === 0) {
        toast.error('Nenhum email selecionado para conversão');
        return;
      }
      
      const convertPromises = emails.map(async (emailData) => {
        const dealData = {
          title: `Oportunidade - ${emailData.company || emailData.organization || emailData.name || emailData.email.split('@')[0]}`,
          value: 0,
          person_name: `${emailData.first_name || ''} ${emailData.last_name || ''}`.trim() || emailData.name || emailData.email.split('@')[0],
          person_email: emailData.email,
          organization_name: emailData.company || emailData.organization || '',
          pipeline_id: selectedPipeline,
          source: 'email_finder'
        };
        
        console.log('💼 Converting email to deal:', dealData);
        console.log('🔗 API object:', api);
        console.log('🌍 Backend URL:', process.env.REACT_APP_BACKEND_URL);
        
        return api.post('/api/deals', dealData);
      });

      await Promise.all(convertPromises);
      toast.success(`🚀 ${emails.length} email(s) convertido(s) para negócios com sucesso!`);
      setShowConvertModal(false);
      setSelectedEmails([]);
      
    } catch (error) {
      console.error('❌ Error converting to deals:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erro desconhecido';
      toast.error(`Erro ao converter emails para negócios: ${errorMessage}`);
    } finally {
      setConvertLoading(false);
    }
  };

  const handleBulkConvert = async () => {
    let emailsToConvert = [];

    // Gather emails from both single and bulk results
    if (selectedEmails.length > 0) {
      emailsToConvert = selectedEmails;
    } else {
      // Use all found emails if none selected
      emailsToConvert = [
        ...searchResults,
        ...bulkResults.flatMap(result => result.emails || [])
      ];
    }

    if (emailsToConvert.length === 0) {
      toast.error('Nenhum email encontrado para conversão');
      return;
    }

    if (convertType === 'lead') {
      if (!selectedFunnel) {
        toast.error('Selecione um funil para os leads');
        return;
      }
      await handleConvertToLead(emailsToConvert);
    } else {
      if (!selectedPipeline) {
        toast.error('Selecione um pipeline para os negócios');
        return;
      }
      await handleConvertToDeal(emailsToConvert);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'bg-green-100 text-green-800';
    if (confidence >= 60) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 80) return 'Alta';
    if (confidence >= 60) return 'Média';
    return 'Baixa';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Busca de Emails</h1>
          <p className="text-slate-600 mt-1">Encontre e verifique emails de prospects com alta precisão</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button 
            onClick={() => fetchSearchHistory()}
            variant="outline"
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            📊 Histórico
          </Button>
          <Button 
            onClick={() => {
              setSelectedEmails([]);
              setShowConvertModal(true);
            }}
            variant="outline"
            className="border-green-300 text-green-600 hover:bg-green-50"
            disabled={searchResults.length === 0 && bulkResults.length === 0}
          >
            🎯 Converter para CRM
          </Button>
          <Button 
            onClick={() => exportResults(searchResults.length > 0 ? searchResults : bulkResults, 'emails-encontrados.csv')}
            variant="outline"
            className="border-purple-300 text-purple-600 hover:bg-purple-50"
            disabled={searchResults.length === 0 && bulkResults.length === 0}
          >
            📥 Exportar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.total_searches}</div>
              <div className="text-sm text-slate-600">Buscas Realizadas</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.found_emails}</div>
              <div className="text-sm text-slate-600">Emails Encontrados</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.verified_emails}</div>
              <div className="text-sm text-slate-600">Emails Verificados</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.confidence_rate}%</div>
              <div className="text-sm text-slate-600">Taxa de Confiança</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex space-x-6">
          {[
            { id: 'domain', label: '🌐 Busca por Domínio' },
            { id: 'single', label: '🎯 Busca Individual' },
            { id: 'bulk', label: '📊 Busca em Lote' },
            { id: 'verify', label: '✅ Verificação' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Domain Search Tab */}
        {activeTab === 'domain' && (
          <div className="space-y-6">
            <Card className="glass border-white/20">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <span>🌐</span>
                  <span>Busca por Domínio</span>
                </CardTitle>
                <CardDescription>
                  Encontre emails específicos para um domínio (limite: 20 por busca)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Domínio *</Label>
                    <Input
                      value={domainSearch.domain}
                      onChange={(e) => setDomainSearch({...domainSearch, domain: e.target.value})}
                      placeholder="Ex: empresa.com"
                      className="font-mono"
                    />
                    <p className="text-xs text-slate-500">
                      💡 Digite apenas o domínio (sem www ou http). <br />
                      ⚠️ Limite de 20 emails por busca para preservar créditos Hunter.io
                    </p>
                  </div>

                  <div className="mt-6">
                    <Button 
                      onClick={handleDomainSearch}
                      disabled={domainSearch.loading || !domainSearch.domain.trim()}
                      className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white"
                    >
                      {domainSearch.loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Buscando...
                        </>
                      ) : (
                        <>🔍 Buscar Emails do Domínio</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Domain Search Results */}
            {domainSearch.results && (
              <Card>
                <CardHeader>
                  <CardTitle>Resultados da Busca por Domínio</CardTitle>
                  <CardDescription>
                    {domainSearch.results.total_emails} emails encontrados para {domainSearch.results.domain}
                    {domainSearch.pagination?.excluded_count > 0 && (
                      <span className="text-orange-600"> • {domainSearch.pagination.excluded_count} duplicatas filtradas</span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {domainSearch.results.emails && domainSearch.results.emails.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {domainSearch.results.emails.map((email, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium">📧</span>
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">{email.email}</div>
                              <div className="text-sm text-slate-600">
                                {email.first_name} {email.last_name} • {email.position || 'N/A'}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <Badge className={getConfidenceColor(email.confidence || 75)}>
                              {email.confidence || 75}% {getConfidenceLabel(email.confidence || 75)}
                            </Badge>
                            {/* Source indicator badge */}
                            <Badge 
                              variant="outline" 
                              className={
                                email.source === 'mock_data' 
                                  ? 'border-orange-300 text-orange-700 bg-orange-50' 
                                  : email.source === 'hunter.io'
                                  ? 'border-green-300 text-green-700 bg-green-50'
                                  : 'border-blue-300 text-blue-700 bg-blue-50'
                              }
                            >
                              {email.source === 'mock_data' ? '⚠️ Simulado' : 
                               email.source === 'hunter.io' ? '✅ Hunter.io' : 
                               '🔍 API'}
                            </Badge>
                            <Button 
                              size="sm"
                              onClick={() => navigator.clipboard.writeText(email.email)}
                              variant="outline"
                            >
                              📋 Copiar
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedEmails([email]);
                                setShowConvertModal(true);
                              }}
                              className="bg-green-100 text-green-700 hover:bg-green-200"
                            >
                              🎯 Converter
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <div className="text-4xl mb-2">🔍</div>
                      <p className="text-lg font-medium mb-2">Nenhum email encontrado para este domínio</p>
                      <div className="text-sm space-y-1">
                        <p>⚠️ <strong>Isso é normal!</strong> Hunter.io só retorna emails encontrados publicamente na web.</p>
                        <p>Algumas empresas podem ter:</p>
                        <p>• Emails privados não indexados publicamente</p>
                        <p>• Política de privacidade rigorosa</p>
                        <p>• Poucos dados disponíveis na internet</p>
                        <p className="mt-4 text-blue-600">💡 <strong>Dica:</strong> Tente domínios maiores como microsoft.com ou tesla.com para teste</p>
                      </div>
                    </div>
                  )}
                  
                  {domainSearch.results.total_emails > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col space-y-1">
                          <div className="text-sm text-slate-600">
                            Total: {domainSearch.results.total_emails} emails encontrados
                          </div>
                          {domainSearch.pagination?.has_more && (
                            <div className="text-xs text-orange-600">
                              📊 Mais emails disponíveis - clique em "Buscar Mais" para continuar
                            </div>
                          )}
                          {domainSearch.pagination?.total_available && (
                            <div className="text-xs text-slate-500">
                              Hunter.io encontrou {domainSearch.pagination.total_available} emails totais para este domínio
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          {domainSearch.pagination?.has_more && (
                            <Button 
                              size="sm"
                              onClick={() => handleDomainSearch(true)}
                              disabled={domainSearch.loading}
                              className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                            >
                              {domainSearch.loading ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700 mr-1"></div>
                                  Buscando...
                                </>
                              ) : (
                                <>🔄 Buscar Mais (+20)</>
                              )}
                            </Button>
                          )}
                          <Button 
                            size="sm"
                            onClick={() => exportResults(domainSearch.results.emails || [], `emails-${domainSearch.results.domain}.csv`)}
                            variant="outline"
                            className="border-purple-300 text-purple-600 hover:bg-purple-50"
                          >
                            📥 Exportar
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => {
                              setSelectedEmails(domainSearch.results.emails || []);
                              setShowConvertModal(true);
                            }}
                            className="bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            🎯 Converter Todos
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Single Search Tab */}
        {activeTab === 'single' && (
          <div className="space-y-6">
            <Card className="glass border-white/20">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <span>🎯</span>
                  <span>Busca Individual de Email</span>
                </CardTitle>
                <CardDescription>
                  Encontre o email de uma pessoa específica informando nome e empresa
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input
                      value={singleSearch.first_name}
                      onChange={(e) => setSingleSearch({...singleSearch, first_name: e.target.value})}
                      placeholder="Ex: João"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Sobrenome</Label>
                    <Input
                      value={singleSearch.last_name}
                      onChange={(e) => setSingleSearch({...singleSearch, last_name: e.target.value})}
                      placeholder="Ex: Silva"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Empresa *</Label>
                    <Input
                      value={singleSearch.company}
                      onChange={(e) => setSingleSearch({...singleSearch, company: e.target.value})}
                      placeholder="Ex: Microsoft"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Domínio (Opcional)</Label>
                    <Input
                      value={singleSearch.domain}
                      onChange={(e) => setSingleSearch({...singleSearch, domain: e.target.value})}
                      placeholder="Ex: microsoft.com"
                    />
                    <p className="text-xs text-orange-600">
                      ⚠️ <strong>Importante:</strong> Adicione o domínio para busca real via Hunter.io. 
                      Sem domínio = email simulado!
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <Button 
                    onClick={handleSingleSearch}
                    disabled={loading || !singleSearch.first_name || !singleSearch.company}
                    className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Buscando...
                      </>
                    ) : (
                      <>🔍 Buscar Email</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Single Search Results */}
            {searchResults.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Resultados da Busca</CardTitle>
                  <CardDescription>Emails encontrados com níveis de confiança</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {searchResults.map((result, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium">📧</span>
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">{result.email}</div>
                            <div className="text-sm text-slate-600">
                              {result.source} • {result.type || 'Professional'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Badge className={getConfidenceColor(result.confidence)}>
                            {result.confidence}% {getConfidenceLabel(result.confidence)}
                          </Badge>
                          {/* Source indicator badge for single search */}
                          <Badge 
                            variant="outline" 
                            className={
                              result.source === 'mock_data' 
                                ? 'border-orange-300 text-orange-700 bg-orange-50' 
                                : result.source === 'hunter.io'
                                ? 'border-green-300 text-green-700 bg-green-50'
                                : 'border-blue-300 text-blue-700 bg-blue-50'
                            }
                          >
                            {result.source === 'mock_data' ? '⚠️ Simulado' : 
                             result.source === 'hunter.io' ? '✅ Hunter.io' : 
                             '🔍 API'}
                          </Badge>
                          <Button 
                            size="sm"
                            onClick={() => navigator.clipboard.writeText(result.email)}
                            variant="outline"
                          >
                            📋 Copiar
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => {
                              setSelectedEmails([result]);
                              setShowConvertModal(true);
                            }}
                            className="bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            🎯 Converter
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Bulk Search Tab */}
        {activeTab === 'bulk' && (
          <div className="space-y-6">
            <Card className="glass border-white/20">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <span>📊</span>
                  <span>Busca em Lote</span>
                </CardTitle>
                <CardDescription>
                  Processe múltiplos nomes de uma vez. Formato: "Nome Sobrenome, Empresa" (uma por linha)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Lista de Nomes e Empresas</Label>
                    <Textarea
                      value={bulkList}
                      onChange={(e) => setBulkList(e.target.value)}
                      placeholder="João Silva, Microsoft&#10;Maria Santos, Google&#10;Pedro Oliveira, Meta"
                      rows={8}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500">
                      💡 Formato: "Nome Sobrenome, Empresa" - Uma entrada por linha
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      {bulkList.split('\n').filter(line => line.trim()).length} registros para processar
                    </div>
                    <Button 
                      onClick={handleBulkSearch}
                      disabled={bulkLoading || !bulkList.trim()}
                      className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
                    >
                      {bulkLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processando...
                        </>
                      ) : (
                        <>🚀 Processar Lista</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Results */}
            {bulkResults.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Resultados do Processamento</CardTitle>
                  <CardDescription>
                    {bulkResults.filter(r => r.emails && r.emails.length > 0).length} de {bulkResults.length} com emails encontrados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {bulkResults.map((result, index) => (
                      <div key={index} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-slate-900">
                            {result.first_name} {result.last_name} - {result.company}
                          </div>
                          <Badge variant="secondary">
                            {result.emails ? result.emails.length : 0} email(s)
                          </Badge>
                        </div>
                        {result.emails && result.emails.length > 0 ? (
                          <div className="space-y-2">
                            {result.emails.map((email, emailIndex) => (
                              <div key={emailIndex} className="flex items-center justify-between text-sm">
                                <span className="text-slate-700">{email.email}</span>
                                <Badge className={getConfidenceColor(email.confidence)}>
                                  {email.confidence}%
                                </Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-500">Nenhum email encontrado</div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Verification Tab */}
        {activeTab === 'verify' && (
          <div className="space-y-6">
            <Card className="glass border-white/20">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <span>✅</span>
                  <span>Verificação de Emails</span>
                </CardTitle>
                <CardDescription>
                  Verifique se os emails são válidos e deliverable
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Lista de Emails para Verificação</Label>
                    <Textarea
                      value={emailsToVerify}
                      onChange={(e) => setEmailsToVerify(e.target.value)}
                      placeholder="joao@empresa.com&#10;maria@outraempresa.com&#10;pedro@exemplo.com"
                      rows={8}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500">
                      💡 Um email por linha
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      {emailsToVerify.split('\n').filter(email => email.trim()).length} emails para verificar
                    </div>
                    <Button 
                      onClick={handleEmailVerification}
                      disabled={loading || !emailsToVerify.trim()}
                      className="bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Verificando...
                        </>
                      ) : (
                        <>✅ Verificar Emails</>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Verification Results */}
            {verificationResults.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Resultados da Verificação</CardTitle>
                  <CardDescription>
                    {verificationResults.filter(r => r.status === 'valid').length} de {verificationResults.length} emails válidos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {verificationResults.map((result, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            result.status === 'valid' ? 'bg-green-500' : 
                            result.status === 'invalid' ? 'bg-red-500' : 'bg-yellow-500'
                          }`}></div>
                          <span className="text-slate-900">{result.email}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge className={
                            result.status === 'valid' ? 'bg-green-100 text-green-800' :
                            result.status === 'invalid' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                          }>
                            {result.status === 'valid' ? 'Válido' : 
                             result.status === 'invalid' ? 'Inválido' : 'Desconhecido'}
                          </Badge>
                          {result.confidence && (
                            <Badge variant="secondary">{result.confidence}%</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      
      {/* Convert to CRM Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>🎯</span>
              <span>Converter Emails para CRM</span>
            </DialogTitle>
            <DialogDescription>
              Transforme emails encontrados em leads ou negócios no sistema CRM
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Conversion Type Selection */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Tipo de Conversão</Label>
              
              <div className="grid grid-cols-2 gap-4">
                <Card 
                  className={`cursor-pointer transition-all ${
                    convertType === 'lead' 
                      ? 'ring-2 ring-blue-500 bg-blue-50' 
                      : 'hover:bg-slate-50'
                  }`}
                  onClick={() => setConvertType('lead')}
                >
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl mb-2">👥</div>
                    <h3 className="font-medium">Converter para Leads</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Para qualificação e nutrição
                    </p>
                  </CardContent>
                </Card>
                
                <Card 
                  className={`cursor-pointer transition-all ${
                    convertType === 'deal' 
                      ? 'ring-2 ring-green-500 bg-green-50' 
                      : 'hover:bg-slate-50'
                  }`}
                  onClick={() => setConvertType('deal')}
                >
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl mb-2">💼</div>
                    <h3 className="font-medium">Converter para Negócios</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Para pipeline de vendas
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Source Information */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium text-slate-800 mb-2">📧 Emails para Conversão</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Origem:</span>
                  <span className="font-medium">Email Finder</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Emails encontrados:</span>
                  <span className="font-medium">
                    {searchResults.length + bulkResults.reduce((acc, r) => acc + (r.emails?.length || 0), 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Selecionados:</span>
                  <span className="font-medium text-blue-600">
                    {selectedEmails.length > 0 ? selectedEmails.length : 'Todos encontrados'}
                  </span>
                </div>
              </div>
            </div>

            {/* Destination Configuration */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">
                {convertType === 'lead' ? '🎯 Configuração de Leads' : '🚀 Configuração de Negócios'}
              </Label>
              
              {convertType === 'lead' ? (
                <div className="space-y-2">
                  <Label>Funil de Destino *</Label>
                  <Select 
                    value={selectedFunnel}
                    onValueChange={setSelectedFunnel}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um funil para os leads" />
                    </SelectTrigger>
                    <SelectContent>
                      {funnels.map((funnel) => (
                        <SelectItem key={funnel.id} value={funnel.id}>
                          {funnel.name} ({funnel.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Pipeline de Destino *</Label>
                  <Select 
                    value={selectedPipeline}
                    onValueChange={setSelectedPipeline}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um pipeline para os negócios" />
                    </SelectTrigger>
                    <SelectContent>
                      {pipelines.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name} • {pipeline.stages?.length || 0} estágios
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Email Confidence Info */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-800 mb-2">✨ Qualidade dos Emails</h4>
              <div className="space-y-1 text-sm text-green-700">
                <p>• Emails com alta confiança (80%+) serão priorizados</p>
                <p>• Informações de empresa e cargo serão preservadas</p>
                <p>• Fonte de origem será registrada para rastreamento</p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleBulkConvert}
              disabled={convertLoading || 
                       (searchResults.length === 0 && bulkResults.length === 0) ||
                       (convertType === 'lead' && !selectedFunnel) ||
                       (convertType === 'deal' && !selectedPipeline)}
              className={`${
                convertType === 'lead' 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700' 
                  : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
              } text-white`}
            >
              {convertLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Convertendo...
                </>
              ) : (
                <>
                  {convertType === 'lead' ? '🎯 Converter para Leads' : '🚀 Converter para Negócios'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}